import React, { ElementType } from "react";
import fullWhiteLogo from "../../assets/nudge-white-hand.png";
import {
  ButtonVariant,
  SocialIcon,
  Button,
  SocialIconVariant,
  Typography,
} from "../../atoms";
import { clsx } from "clsx";

/**
 * This footer is used for the Score App and the Landing App - the Main App has a different footer
 */

type ScoreAppFooterProps = {
  links: {
    title: string;
    href: string;
  }[];
  LinkComponent: ElementType;
  ImageComponent: ElementType;
  withCtaButton?: boolean;
  onCtaClick?: () => void;
  ctaText?: string;
  className?: string;
};

export const ScoreAppFooter: React.FC<ScoreAppFooterProps> = ({
  links,
  LinkComponent,
  ImageComponent,
  withCtaButton = false,
  ctaText,
  onCtaClick,
  className,
}) => {
  return (
    <footer className={`bg-neutral-950 ${clsx(className)}`}>
      <div className="max-w-screen-xl mx-auto">
        {/* 1st row: CTA and hand */}
        <div
          className={`flex justify-between mb-10 ${clsx({ "md:mb-16": withCtaButton })}`}
        >
          <div className="flex flex-col">
            <Typography
              variant="p-subheading"
              breakpointVariants={{ sm: "h4" }}
              className="mb-6 md:mb-10 text-neutral-0"
            >
              Find your true value
            </Typography>
            {withCtaButton && ctaText && (
              <div>
                <Button onClick={onCtaClick} variant={ButtonVariant.DEFAULT}>
                  {ctaText}
                </Button>
              </div>
            )}
          </div>
          <ImageComponent
            src={fullWhiteLogo}
            alt="Hand Image"
            width={80}
            height={104}
            className="w-[53px] h-[64px] md:w-[80px] md:h-[104px]"
          />
        </div>
        {/* 2nd row: description and legal links */}
        <div className="flex justify-between flex-wrap mb-10 md:mb-16">
          <div className="max-w-sm mr-24">
            <Typography
              variant="p-leading"
              className="text-neutral-600 font-medium mb-4"
            >
              Nudge
            </Typography>
            <Typography
              variant="p-sm"
              breakpointVariants={{ md: "p" }}
              className="text-neutral-800 mb-10"
            >
              Programmable Nudges financialize every decision and redistribute
              value to users. In return, protocols receive a scalable customer
              acquisition funnel & retention mechanism.
            </Typography>
          </div>
          <div className="flex flex-col sm:items-start md:items-end">
            {links.map(({ title, href }, index) => (
              <Typography
                key={title}
                variant="p-sm"
                className={`text-neutral-800 font-medium mb-5 ${index === 0 ? "mt-2" : ""}`}
              >
                <LinkComponent href={href}>{title}</LinkComponent>
              </Typography>
            ))}
          </div>
        </div>
        {/* 3rd row: Contact + Copyright */}
        <div className="flex flex-col-reverse md:flex-row justify-between md:items-end">
          <div>
            <Typography
              variant="p-leading"
              className="text-neutral-600 font-medium mb-4"
            >
              Contact
            </Typography>
            <div className="flex flex-nowrap mr-4">
              <a href="https://x.com/nudgexyz" aria-label="x" target="_blank">
                <SocialIcon variant={SocialIconVariant.X} />
              </a>
              <a
                href="https://t.me/+WpBYXE_HIzY0ZmEx"
                aria-label="Telegram"
                target="_blank"
                className="px-3"
              >
                <SocialIcon variant={SocialIconVariant.TELEGRAM} />
              </a>
            </div>
          </div>
          <Typography
            variant="p-sm"
            breakpointVariants={{ md: "p" }}
            className="text-neutral-800 mb-10 md:mb-0"
          >
            Copyright &copy; 2024 Nudge. All rights reserved.
          </Typography>
        </div>
      </div>
    </footer>
  );
};
